/**
 * Sheet lateral para busca contextual de mensagens (estilo WhatsApp Web).
 * - Debounce de 350ms no input.
 * - Filtros rápidos: Tudo / Recebidas / Enviadas / Mídia.
 * - Paginação infinita (10 em 10) com IntersectionObserver.
 * - Snippet com highlight via <mark> retornado pelo backend.
 * - Clique em resultado → scrollToMessage do ChatArea (callback).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Loader2, ImageIcon, Inbox, Send as SendIcon, MessagesSquare } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useConversationMessageSearch,
  type MessageSearchFilter,
  type ConversationSearchResult,
} from '@/hooks/chat/useConversationMessageSearch';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ConversationSearchSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string | undefined;
  /** Pode ser síncrono (scroll na DOM) ou assíncrono (jump via RPC ancorada). */
  onResultClick: (messageId: string, sentAt: string) => void | Promise<void>;
  /** ID da mensagem cujo jump-to está em andamento (renderiza spinner inline no item). */
  jumpingMessageId?: string | null;
}

const FILTERS: { key: MessageSearchFilter; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'all', label: 'Tudo', icon: MessagesSquare },
  { key: 'inbound', label: 'Recebidas', icon: Inbox },
  { key: 'outbound', label: 'Enviadas', icon: SendIcon },
  { key: 'media', label: 'Mídia', icon: ImageIcon },
];

function formatSentAt(iso: string): string {
  const d = new Date(iso);
  if (isToday(d)) return format(d, "HH:mm", { locale: ptBR });
  if (isYesterday(d)) return `Ontem ${format(d, 'HH:mm', { locale: ptBR })}`;
  return format(d, "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
}

function SnippetRenderer({ html }: { html: string | null }) {
  // O snippet vem com tags <mark> seguras (gerado pelo Postgres ts_headline).
  if (!html) return <span className="text-muted-foreground italic">(sem texto)</span>;
  return (
    <span
      className="text-sm leading-snug line-clamp-3 [&_mark]:bg-primary/20 [&_mark]:text-foreground [&_mark]:rounded [&_mark]:px-0.5 [&_mark]:font-medium"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function ConversationSearchSheet({
  open,
  onOpenChange,
  conversationId,
  onResultClick,
  jumpingMessageId,
}: ConversationSearchSheetProps) {
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filter, setFilter] = useState<MessageSearchFilter>('all');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce 350ms para evitar requisições excessivas
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(rawQuery), 350);
    return () => clearTimeout(t);
  }, [rawQuery]);

  // Reset ao fechar / trocar de conversa
  useEffect(() => {
    if (!open) {
      setRawQuery('');
      setDebouncedQuery('');
      setFilter('all');
    } else {
      // Foco automático ao abrir
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, conversationId]);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useConversationMessageSearch({
    conversationId,
    query: debouncedQuery,
    filter,
    enabled: open,
  });

  // IntersectionObserver para paginação infinita
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '120px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, data]);

  const allItems: ConversationSearchResult[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );
  const total = data?.pages[0]?.total ?? 0;
  const hasQuery = debouncedQuery.trim().length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b space-y-3">
          <SheetTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" />
            Pesquisar na conversa
          </SheetTitle>

          {/* Input de busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder="Buscar mensagens, links, nomes..."
              className="pl-9 pr-9 h-10 bg-muted/40"
              maxLength={200}
            />
            {rawQuery && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-muted text-muted-foreground"
                aria-label="Limpar busca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Filtros rápidos (pílulas) */}
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <Button
                  key={f.key}
                  type="button"
                  variant={active ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-7 rounded-full text-xs gap-1.5 whitespace-nowrap px-3',
                    !active && 'bg-muted/40 border-muted hover:bg-muted',
                  )}
                  onClick={() => setFilter(f.key)}
                >
                  <Icon className="h-3 w-3" />
                  {f.label}
                </Button>
              );
            })}
          </div>

          {/* Contador */}
          {hasQuery && !isFetching && (
            <div className="text-xs text-muted-foreground">
              {total === 0
                ? 'Nenhum resultado encontrado'
                : `${total} resultado${total === 1 ? '' : 's'} encontrado${total === 1 ? '' : 's'}`}
            </div>
          )}
        </SheetHeader>

        {/* Lista de resultados */}
        <ScrollArea className="flex-1">
          <div className="px-2 py-2">
            {/* Estado vazio inicial */}
            {!hasQuery && (
              <div className="flex flex-col items-center justify-center text-center py-16 px-6 text-muted-foreground">
                <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                  <Search className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium text-foreground/80">
                  Pesquise nesta conversa
                </p>
                <p className="text-xs mt-1">
                  Digite uma palavra ou frase para encontrar mensagens trocadas.
                </p>
              </div>
            )}

            {/* Loading inicial */}
            {hasQuery && isFetching && allItems.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {/* Erro */}
            {isError && (
              <div className="text-sm text-destructive text-center py-8 px-4">
                Erro ao buscar mensagens: {(error as Error)?.message || 'tente novamente'}
              </div>
            )}

            {/* Nenhum resultado */}
            {hasQuery && !isFetching && allItems.length === 0 && !isError && (
              <div className="flex flex-col items-center justify-center text-center py-16 px-6 text-muted-foreground">
                <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                  <Search className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium text-foreground/80">Nada por aqui</p>
                <p className="text-xs mt-1">
                  Tente outras palavras-chave ou ajuste os filtros.
                </p>
              </div>
            )}

            {/* Resultados */}
            <ul className="space-y-1">
              {allItems.map((item) => {
                const isInbound = item.direction === 'inbound';
                const initials = (item.sender_name || '?')
                  .split(' ')
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();

                const isJumping = jumpingMessageId === item.id;
                const isAnyJumping = Boolean(jumpingMessageId);

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      disabled={isAnyJumping}
                      onClick={async () => {
                        // Aguarda jump (sincrono ou async via RPC ancorada).
                        // Só fecha a sheet APÓS concluir, para o spinner ser visível
                        // no item enquanto a janela ancorada é carregada.
                        await onResultClick(item.id, item.sent_at);
                        onOpenChange(false);
                      }}
                      className="w-full text-left rounded-lg px-2.5 py-2.5 hover:bg-muted/60 transition-colors flex gap-2.5 items-start group disabled:opacity-60 disabled:cursor-wait"
                    >
                      <Avatar className="h-8 w-8 flex-shrink-0 mt-0.5">
                        {item.sender_avatar && <AvatarImage src={item.sender_avatar} />}
                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                          {initials}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-xs font-medium truncate text-foreground/90">
                            {item.sender_name || (isInbound ? 'Contato' : 'Você')}
                          </span>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0 flex items-center gap-1">
                            {isJumping && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                            {formatSentAt(item.sent_at)}
                          </span>
                        </div>

                        <SnippetRenderer html={item.snippet} />

                        {item.message_type !== 'text' && (
                          <Badge
                            variant="secondary"
                            className="mt-1 h-4 text-[9px] px-1.5 gap-1 font-normal"
                          >
                            <ImageIcon className="h-2.5 w-2.5" />
                            {item.message_type}
                          </Badge>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Sentinel para paginação infinita */}
            {hasNextPage && (
              <div ref={sentinelRef} className="flex items-center justify-center py-4">
                {isFetchingNextPage && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            )}

            {/* Fim da lista */}
            {!hasNextPage && allItems.length > 0 && (
              <div className="text-center text-[10px] text-muted-foreground py-3">
                — fim dos resultados —
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
